import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGroupsStore } from '../store/groups';
import { PRESET_COLORS } from '../lib/colors';

export function GroupManager() {
  const { t } = useTranslation();
  const groups = useGroupsStore((s) => s.groups);
  const loaded = useGroupsStore((s) => s.loaded);
  const load = useGroupsStore((s) => s.load);
  const create = useGroupsStore((s) => s.create);
  const update = useGroupsStore((s) => s.update);
  const remove = useGroupsStore((s) => s.remove);

  const [name, setName] = useState('');
  const [abbr, setAbbr] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  const handleCreate = async () => {
    if (!name.trim() || !abbr.trim()) {
      setError(t('groups.validationError'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await create({ name: name.trim(), abbr: abbr.trim(), color });
      setName('');
      setAbbr('');
      setColor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('groups.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(t('groups.deleteConfirm', { label }))) return;
    await remove(id);
  };

  return (
    <div>
      <p className="text-xs text-daynote-text/50 mb-4 leading-relaxed">
        {t('groups.desc')}
      </p>

      <div className="space-y-2 mb-5">
        {groups.length === 0 && (
          <div className="text-sm text-daynote-muted py-2">{t('groups.empty')}</div>
        )}
        {groups.map((g) => (
          <div
            key={g.id}
            className="flex items-center gap-2 py-2 border-b border-[var(--grid-line)]"
          >
            <input
              value={g.abbr}
              onChange={(e) => update(g.id, { abbr: e.target.value }).catch(() => load())}
              className="w-16 bg-white/[0.04] border border-[var(--grid-line)] rounded px-2 py-1 text-xs text-daynote-accent focus:outline-none focus:border-daynote-accent"
            />
            <input
              value={g.name}
              onChange={(e) => update(g.id, { name: e.target.value })}
              className="flex-1 bg-white/[0.04] border border-[var(--grid-line)] rounded px-2 py-1 text-sm focus:outline-none focus:border-daynote-accent"
            />
            <div className="flex gap-1 items-center">
              {PRESET_COLORS.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => update(g.id, { color: c })}
                  className={`w-4 h-4 rounded-full ${g.color === c ? 'ring-2 ring-white' : ''}`}
                  style={{
                    background: c ?? 'transparent',
                    border: c ? 'none' : '1.5px dashed #b8aa95',
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleDelete(g.id, `${g.name}`)}
              className="text-xs text-daynote-muted hover:text-daynote-work bg-transparent border-none cursor-pointer"
            >
              {t('groups.delete')}
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={abbr}
          onChange={(e) => setAbbr(e.target.value)}
          placeholder={t('groups.abbr')}
          className="w-20 bg-white/[0.04] border border-[var(--grid-line)] rounded px-2 py-1.5 text-xs focus:outline-none focus:border-daynote-accent"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('groups.name')}
          className="flex-1 min-w-32 bg-white/[0.04] border border-[var(--grid-line)] rounded px-2 py-1.5 text-sm focus:outline-none focus:border-daynote-accent"
        />
        <div className="flex gap-1 items-center">
          {PRESET_COLORS.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setColor(c)}
              className={`w-4 h-4 rounded-full ${color === c ? 'ring-2 ring-white' : ''}`}
              style={{
                background: c ?? 'transparent',
                border: c ? 'none' : '1.5px dashed #b8aa95',
              }}
            />
          ))}
        </div>
        <button
          type="button"
          disabled={submitting}
          onClick={handleCreate}
          className="px-3 py-1.5 text-xs bg-daynote-accent text-[#2a2520] rounded font-medium border-none cursor-pointer disabled:opacity-60"
        >
          {t('groups.add')}
        </button>
      </div>
      {error && <div className="text-xs text-daynote-work mt-2">{error}</div>}
    </div>
  );
}
